import { useState, useEffect, useMemo } from 'react';
import { useRecoilState } from 'recoil';
import { useProfile } from 'hooks/useProfile';
import {
  isOrganizationFollowedState,
  useSetOrganizationFollowedState,
} from 'state/followed-organizations/followed-organizations.state';
import { LeaderRequest, MemberRequest, OrganizationSummary, Event } from 'services/api';
import {
  useOrganizationData,
  useSchoolData,
  useAvailableOrganizations,
  useOrganizationEvents,
} from './useOrganizationModalData';
import { useMyLeaderRequest, useLeaderRequestEligibility, useLeaderRequestActions } from './useLeaderRequest';
import { useMyMembershipRequest, useMembershipRequestActions } from './useMembershipRequest';

// Helper to filter active organization leaders
interface OrgLeader {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  orgRole: string;
  inviteStatus?: string;
  isActive?: boolean;
}

const filterActiveLeaders = (leaders: OrgLeader[] | undefined): OrgLeader[] => {
  if (!leaders) return [];
  return leaders.filter(leader => {
    const isAccepted = leader.inviteStatus?.toLowerCase() === 'accepted';
    const isActive = leader.isActive !== false;
    return isAccepted && isActive;
  });
};

// Helper to safely get role from leader request
const getLeaderRequestRole = (request: LeaderRequest | null | undefined): string => {
  if (!request) return '';
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const role = (request ).role;
  return typeof role === 'string' ? role : '';
};

export interface UseOrganizationModalStateParams {
  open: boolean;
  initialOrganizationId: string;
  organizationName?: string;
  organizationLogoUrl?: string;
  schoolSlug?: string;
  onMembershipChange?: () => void;
}

export interface UseOrganizationModalStateResult {
  // State
  isLeadersExpanded: boolean;
  setIsLeadersExpanded: (expanded: boolean) => void;
  selectedOrganizationId: string;
  setSelectedOrganizationId: (id: string) => void;
  isRoleModalOpen: boolean;
  setIsRoleModalOpen: (open: boolean) => void;
  isEditMode: boolean;
  setIsEditMode: (mode: boolean) => void;

  // Data
  availableOrganizations: OrganizationSummary[];
  orgLeaders: OrgLeader[];
  events: Event[];
  isLoading: boolean;
  isFollowed: boolean;
  myLeaderRequest: LeaderRequest | null | undefined;
  isEligibleForLeaderRequest: boolean;
  hasPendingRequest: boolean;

  // Membership state (CAM-669)
  myMembershipRequest: MemberRequest | null | undefined;
  membershipStatus: 'none' | 'pending' | 'approved';
  organizationDescription?: string;
  organizationSchoolName?: string;

  // Display values
  displayName: string;
  displayLogoUrl: string;
  roleModalInitialRole: string;

  // Flags
  showLeadersSection: boolean;
  showFollowButton: boolean;
  /** True when the selected organization belongs to the current user's school. */
  isSameSchool: boolean;

  // Handlers
  handleOrganizationChange: (newOrganizationId: string) => void;
  handleOpenRoleModal: () => void;
  handleEditRequest: () => void;
  handleRequestLeaderAccess: (role: string) => Promise<void>;
  handleUpdateLeaderRequest: (role: string) => Promise<void>;
  handleCancelLeaderRequest: () => Promise<void>;
  handleAcceptInvite: (acceptedTerms: boolean) => Promise<void>;
  handleDeclineInvite: () => Promise<void>;
  /** CAM-684: Self-remove from organization leaders */
  handleLeaveMyRole: () => Promise<void>;
  /** True when the current user is an accepted leader of the selected org. */
  isAcceptedLeader: boolean;
  revalidateLeaderRequest: () => void;

  // Membership handlers (CAM-669)
  handleRequestMembership: () => Promise<void>;
  handleCancelMembershipRequest: () => Promise<void>;
  handleLeaveOrganization: () => Promise<void>;

  // School terms for invite acceptance
  schoolTermsUrl?: string;
  schoolTermsDisplayName?: string;
}

export const useOrganizationModalState = ({
  open,
  initialOrganizationId,
  organizationName,
  organizationLogoUrl,
  schoolSlug,
  onMembershipChange,
}: UseOrganizationModalStateParams): UseOrganizationModalStateResult => {
  const [isLeadersExpanded, setIsLeadersExpanded] = useState(true);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>(initialOrganizationId);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const { profile } = useProfile();

  useEffect(() => {
    if (initialOrganizationId) setSelectedOrganizationId(initialOrganizationId);
  }, [initialOrganizationId]);

  const [isFollowed] = useRecoilState<boolean>(isOrganizationFollowedState(selectedOrganizationId));

  // Fetch data using custom hooks
  const rOrganization = useOrganizationData(open, selectedOrganizationId);
  const organization = rOrganization.data;
  // Keep Recoil in sync with GET /organizations/:id (join, leave, and first open).
  useSetOrganizationFollowedState(organization);
  const schoolIdFromOrg = organization?.schoolId;

  const rSchool = useSchoolData(open, schoolSlug, schoolIdFromOrg);
  const schoolId = schoolIdFromOrg || rSchool.data?.id;
  const availableOrganizations = useAvailableOrganizations(open, schoolId);
  const rEvents = useOrganizationEvents(open, selectedOrganizationId);
  const { data: myLeaderRequest, revalidate: revalidateLeaderRequest } = useMyLeaderRequest(open, selectedOrganizationId);
  const {
    data: myMembershipRequest,
    revalidate: revalidateMembershipRequest,
    setCache: setMembershipRequestCache,
  } = useMyMembershipRequest(open, selectedOrganizationId);

  const events = rEvents.data?.events ?? [];
  const isLoading = rOrganization.isValidating || rEvents.isValidating;

  // Get organization leaders using memoization
  const orgLeaders = useMemo(
    () => filterActiveLeaders(organization?.orgLeaders),
    [organization?.orgLeaders],
  );

  // Leader request hooks
  const { isEligibleForLeaderRequest, hasPendingRequest } = useLeaderRequestEligibility({
    profile,
    selectedOrganizationId,
    orgLeaders,
    myLeaderRequest,
  });

  const {
    handleRequestLeaderAccess,
    handleUpdateLeaderRequest,
    handleCancelLeaderRequest,
    handleAcceptInvite,
    handleDeclineInvite,
    handleLeaveMyRole,
  } = useLeaderRequestActions({
    selectedOrganizationId,
    myLeaderRequest,
    revalidateLeaderRequest,
    revalidateOrganization: rOrganization.mutate,
  });

  const {
    handleRequestMembership,
    handleCancelMembershipRequest,
    handleLeaveOrganization,
  } = useMembershipRequestActions({
    selectedOrganizationId,
    myMembershipRequest,
    revalidateMembershipRequest,
    setMembershipRequestCache,
    revalidateOrganization: rOrganization.mutate,
    onMembershipChange,
  });

  // Whether the current user is an accepted leader of this org. Used for
  // the Organization Leaders section (e.g. showing the "Leave My Role"
  // button). Intentionally independent of general membership status —
  // leaders are not treated as members.
  const isAcceptedLeaderForOrg = Boolean(
    profile?.id && orgLeaders.some(leader => leader.userId === profile.id),
  );

  // Resolve general-membership state for the Members tab (CAM-676/677/678).
  // Prefer the fresh GET /my-membership-request result so cancel/approve flows
  // update the UI immediately; fall back to the DTO's myMembershipStatus.
  // Do NOT treat bare follow as approved — follow and membership are separate.
  const membershipStatus: 'none' | 'pending' | 'approved' = (() => {
    const isAutoAccept = organization?.membershipApprovalMode === 'auto_accept';
    if (myMembershipRequest?.status === 'approved') return 'approved';
    if (myMembershipRequest?.status === 'pending' && !isAutoAccept) {
      return 'pending';
    }
    if (organization?.myMembershipStatus === 'approved') return 'approved';
    if (organization?.myMembershipStatus === 'pending' && !isAutoAccept) {
      return 'pending';
    }
    return 'none';
  })();

  // Compute display values
  const selectedOrg = availableOrganizations.find(org => org.id === selectedOrganizationId);
  const displayName = (selectedOrganizationId === initialOrganizationId && organizationName)
    ? organizationName
    : (selectedOrg?.name || organization?.name || '');
  const displayLogoUrl = (selectedOrganizationId === initialOrganizationId && organizationLogoUrl)
    ? organizationLogoUrl
    : (selectedOrg?.logoUrl || organization?.logoUrl || '');

  // Handlers
  const handleOrganizationChange = (newOrganizationId: string) => setSelectedOrganizationId(newOrganizationId);

  const handleOpenRoleModal = () => {
    setIsEditMode(false);
    setIsRoleModalOpen(true);
  };

  const handleEditRequest = () => {
    setIsEditMode(true);
    setIsRoleModalOpen(true);
  };

  // Show leaders section only if user and organization are from the same school
  // and if there are leaders, eligible for request, or pending requests
  const isSameSchool = Boolean(profile?.school?.id && schoolId && profile.school.id === schoolId);
  const showLeadersSection = isSameSchool && (orgLeaders.length > 0 || isEligibleForLeaderRequest || hasPendingRequest);
  // Members are followers (auto-accept join upserts a follower). Hide Follow
  // as soon as membership is approved, even if Recoil hasn't hydrated yet.
  const isFollowedForUi = isFollowed || membershipStatus === 'approved';
  const showFollowButton = !isFollowedForUi && events.length > 0;
  const roleModalInitialRole = isEditMode ? getLeaderRequestRole(myLeaderRequest) : '';

  return {
    // State
    isLeadersExpanded,
    setIsLeadersExpanded,
    selectedOrganizationId,
    setSelectedOrganizationId,
    isRoleModalOpen,
    setIsRoleModalOpen,
    isEditMode,
    setIsEditMode,

    // Data
    availableOrganizations,
    orgLeaders,
    events,
    isLoading,
    isFollowed: isFollowedForUi,
    myLeaderRequest,
    isEligibleForLeaderRequest,
    hasPendingRequest,

    // Membership
    myMembershipRequest,
    membershipStatus,
    organizationDescription: undefined,
    // CAM-961: About must show the org's school, never the signed-in user's.
    // GET /schools/:id is scoped to the current school, so rSchool is only a
    // same-school fallback. The org payload is the cross-school source of truth.
    organizationSchoolName:
      organization?.school?.name ?? rSchool.data?.name,

    // Display values
    displayName,
    displayLogoUrl,
    roleModalInitialRole,

    // Flags
    showLeadersSection,
    showFollowButton,
    isSameSchool,

    // Handlers
    handleOrganizationChange,
    handleOpenRoleModal,
    handleEditRequest,
    handleRequestLeaderAccess,
    handleUpdateLeaderRequest,
    handleCancelLeaderRequest,
    handleAcceptInvite,
    handleDeclineInvite,
    handleLeaveMyRole: async () => {
      if (profile?.id) await handleLeaveMyRole(profile.id);
    },
    isAcceptedLeader: isAcceptedLeaderForOrg,
    revalidateLeaderRequest,

    // Membership
    handleRequestMembership,
    handleCancelMembershipRequest,
    handleLeaveOrganization,

    // School terms
    schoolTermsUrl: rSchool.data?.termsUrl,
    schoolTermsDisplayName: rSchool.data?.termsDisplayName,
  };
};
